import { useCallback } from 'react';
import { type NavigateOptions, type To, useNavigate } from 'react-router-dom';
import { applyLocaleToPath } from '~/routing/paths';
import { useI18n } from '~/stores/i18n';

type LocalizedNavigateOptions = NavigateOptions & {
  skipLocale?: boolean;
};

type LocalizedNavigateFunction = {
  (to: To, options?: LocalizedNavigateOptions): void;
  (delta: number): void;
};

/**
 * A wrapper around react-router's useNavigate that automatically
 * applies the current locale prefix to pathnames.
 *
 * - String paths: `/experience/123/` -> `/de/experience/123/` (if lang is 'de')
 * - Object paths: { pathname: '/foo/' } -> { pathname: '/de/foo/' }
 * - Numeric delta: navigate(-1) passes through unchanged
 * - English ('en') paths have no prefix (default locale behavior)
 * - Pass `{ skipLocale: true }` to behave like the regular react-router useNavigate
 */
const useLocalizedNavigate = (): LocalizedNavigateFunction => {
  const navigate = useNavigate();
  const { lang } = useI18n();

  return useCallback(
    (to: To | number, options?: LocalizedNavigateOptions) => {
      if (typeof to === 'number') {
        navigate(to);
        return;
      }

      const { skipLocale, ...navigateOptions } = options ?? {};

      // Skip locale: pass through to react-router directly
      if (skipLocale) {
        navigate(to, navigateOptions);
        return;
      }

      if (typeof to === 'string') {
        navigate(applyLocaleToPath(to, lang), navigateOptions);
        return;
      }

      const { pathname, ...rest } = to;
      navigate(
        {
          ...rest,
          pathname: pathname ? applyLocaleToPath(pathname, lang) : undefined,
        },
        navigateOptions,
      );
    },
    [navigate, lang],
  );
};

export default useLocalizedNavigate;
